import { useMemo } from 'react';
import { useWidget } from '@telescope/cassini-hooks';
import { AspectRatio, Box, Container, Flex, Heading, Image } from '@telescope/cassini-ui';
import { Text } from 'components';
import { BracketGrid, BracketGridProvider, RoundNav, getVoteOptionsList } from 'features/bracket-vote-app';
import { BracketViewHeader } from './BracketViewHeader';
import { VoteGroupData, VoteOption } from 'types';
import { ModalsProvider } from 'features';
import { Winner } from 'generated-types/widget';

export const WinnerPage = () => {
  const {
    data: { settings, content, styles },
  } = useWidget<Winner>({ select: (data) => data.snapshot.snapshot_views.winnerPage });
  const { data: widgetData } = useWidget<Winner>({ select: (data) => data.widget });
  const {
    bracket_vote: { vote_groups: voteGroups, rounds, tournament, settings: bracketSettings },
  } = widgetData.bracket.bracketView;
  const { widget_vote_choices: widgetVoteChoices } = widgetData.voteOptions.settings.contestants;

  // Last round is WINNER round. Voting is disabled.
  const totalVotingRounds = rounds.length - 1;
  const activeRound = rounds.length;

  const voteOptions = useMemo(
    () => getVoteOptionsList(voteGroups as VoteGroupData[], widgetVoteChoices, totalVotingRounds),
    [voteGroups, widgetVoteChoices, totalVotingRounds]
  );

  const winner = widgetVoteChoices.find(
    (option: VoteOption) => tournament.winnerReferenceId && option.schema_reference === tournament.winnerReferenceId
  );

  const heroMedia = {
    video: content.heroVideo || winner?.video,
    image: content.heroImage || winner?.image,
  };

  return (
    <>
      <ModalsProvider>
        {settings.isHeroSectionVisible && (
          <Box as="section" className="HeroSection__root" bg="primary.300" mb={4} {...styles.heroSectionRoot}>
            <Container className="HeroSection__container" centerContent py={9} {...styles.heroSectionContainer}>
              <Heading
                className="HeroSection__headline"
                as="h1"
                fontSize={{ base: '18px', md: '30px' }}
                mb={2}
                variant="emphasized"
                {...styles.heroSectionHeadline}>
                {content.heroHeadline}
              </Heading>
              <Text
                className="HeroSection__subheadline"
                fontSize={{ base: '30px', md: '40px' }}
                fontWeight={600}
                fontStyle={'italic'}
                fontFamily={'heading'}
                mb={4}
                {...styles.heroSectionSubheadline}>
                {winner?.name}
              </Text>

              {(heroMedia.video || heroMedia.image) && (
                <Flex className="HeroSection__media" justifyContent="center" width="100%" {...styles.heroSectionMedia}>
                  {heroMedia.video ? (
                    <AspectRatio width="container.sm" maxW={'full'} ratio={16 / 9}>
                      <iframe
                        title={'winner-video'}
                        src={heroMedia.video}
                        frameBorder="0"
                        allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen></iframe>
                    </AspectRatio>
                  ) : (
                    <Image src={heroMedia.image} />
                  )}
                </Flex>
              )}
            </Container>
          </Box>
        )}
        {settings.isBracketSectionVisible && (
          <BracketGridProvider
            {...{ activeRound, totalRounds: totalVotingRounds, voteOptions, winner }}
            bracketType={bracketSettings.bracket_type}>
            <BracketViewHeader />
            <RoundNav />
            <BracketGrid />
          </BracketGridProvider>
        )}
      </ModalsProvider>
    </>
  );
};
